import { useRef, useState } from 'react';
import type { FundSummary } from '@nemo/api';
import {
  STAGE_LABELS,
  onlyObserved,
  stageChoicesOf,
  type FundStageView,
  type StageChoice,
} from '@nemo/core/model';
import { shownStages, type PicklistOption } from '../grid/cells/index.js';
import { PicklistEditor } from '../grid/cells/PicklistEditor.js';
import { StagePicker } from '../grid/cells/StagePicker.js';
import { stageTitle } from '../grid/fundColumns.js';
import { Popover } from '../ui/Popover.js';

/** One label-left property row. The label's column width is the stylesheet's. */
export function PaneRow(props: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="pane-row">
      <span className="pane-row-label">{props.label}</span>
      <span className="pane-row-value">{props.children}</span>
    </div>
  );
}

/** An empty editable value invites the edit rather than showing a dash. */
export function Placeholder(props: { children?: string }): React.JSX.Element {
  return <span className="pane-placeholder">{props.children ?? 'Add…'}</span>;
}

export interface InlineTextProps {
  /** What the value reads as while it is not being edited. */
  display: React.ReactNode;
  /** What the editor starts with, which is the value as text. */
  value: string;
  onCommit: (text: string) => void;
  /** The accessible name of the editor, since the row's label is not one. */
  label: string;
  multiline?: boolean;
  className?: string;
}

/**
 * Click to edit, Enter (single line) or blur to save, Escape to cancel. A
 * textarea never commits on Enter: that key has to stay free to start a line.
 */
export function InlineText(props: InlineTextProps): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const settled = useRef(false);
  const className = props.className === undefined ? '' : ` ${props.className}`;

  if (editing) {
    const commit = (text: string): void => {
      if (settled.current) return;
      settled.current = true;
      setEditing(false);
      props.onCommit(text);
    };
    const onKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ): void => {
      if (props.multiline !== true && event.key === 'Enter') {
        event.preventDefault();
        commit(event.currentTarget.value);
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        settled.current = true;
        setEditing(false);
      }
    };

    return props.multiline === true ? (
      <textarea
        className={`pane-editor${className}`}
        autoFocus
        rows={3}
        defaultValue={props.value}
        aria-label={props.label}
        onBlur={(event) => commit(event.target.value)}
        onKeyDown={onKeyDown}
      />
    ) : (
      <input
        className={`pane-editor${className}`}
        autoFocus
        defaultValue={props.value}
        aria-label={props.label}
        onBlur={(event) => commit(event.target.value)}
        onKeyDown={onKeyDown}
      />
    );
  }

  return (
    <button
      type="button"
      className={`pane-editable${className}`}
      aria-label={props.label}
      onClick={() => {
        settled.current = false;
        setEditing(true);
      }}
    >
      {props.display}
    </button>
  );
}

export interface PickFieldProps {
  label: string;
  options: readonly PicklistOption[];
  selectedIds: readonly string[];
  onCommit: (ids: readonly string[]) => void;
  /** More than one at a time, as stages and sectors are; a lead status is not. */
  multiple?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * The grid's own picklist editor behind a popover rather than a Glide cell
 * overlay: it needs nothing from the grid, only somewhere to hang from. A
 * multi-pick applies its draft on the way out, the same as its Done button,
 * and only Escape discards it; a single pick has nothing else to say, so it
 * commits on the choice.
 */
export function PickField(props: PickFieldProps): React.JSX.Element {
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<readonly string[]>(props.selectedIds);
  const multiple = props.multiple ?? false;

  function close(apply: boolean): void {
    setOpen(false);
    if (apply) props.onCommit(draft);
  }

  return (
    <>
      <button
        ref={setTrigger}
        type="button"
        className={
          props.className === undefined ? 'pane-editable' : `pane-editable ${props.className}`
        }
        aria-label={props.label}
        onClick={() => {
          setDraft(props.selectedIds);
          setOpen(true);
        }}
      >
        {props.children}
      </button>
      {open && trigger !== null && (
        <Popover
          anchor={{ kind: 'element', element: trigger }}
          onClose={(reason) => close(multiple && reason !== 'escape')}
        >
          <PicklistEditor
            multiple={multiple}
            options={props.options}
            selected={draft}
            onToggle={(id) => {
              if (!multiple) {
                setOpen(false);
                props.onCommit([id]);
                return;
              }
              setDraft((current) =>
                current.includes(id)
                  ? current.filter((existing) => existing !== id)
                  : [...current, id],
              );
            }}
            onCommit={() => close(multiple)}
            onRevert={() => close(false)}
          />
        </Popover>
      )}
    </>
  );
}

/**
 * The grid's Stages chips in the DOM: one per stage the fund invests at, a dot
 * where it leads, hollow where only the rounds say so, and each stage's
 * sources on hover.
 */
export function StageChips(props: { stages: readonly FundStageView[] }): React.JSX.Element {
  const shown = shownStages(props.stages);
  if (shown.length === 0) return <Placeholder />;
  return (
    <span className="stage-chips">
      {shown.map((entry) => (
        <span
          key={entry.stage}
          className={
            onlyObserved(entry) || !entry.participates
              ? 'stage-chip stage-chip-hollow'
              : 'stage-chip'
          }
          title={stageTitle(entry)}
        >
          {entry.leads && <span className="lead-dot" aria-label="leads" />}
          {STAGE_LABELS[entry.stage]}
          {entry.conflicts.length > 0 && (
            <span className="conflict-flag" aria-label="newer evidence disagrees">
              !
            </span>
          )}
        </span>
      ))}
    </span>
  );
}

/**
 * The stages behind the grid's picker, in a popover: two boxes per stage. It
 * applies on the way out, as a multi-pick does, and only Escape discards.
 */
export function StageField(props: {
  fund: FundSummary;
  onCommit: (choices: readonly StageChoice[]) => void;
}): React.JSX.Element {
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null);
  const [draft, setDraft] = useState<readonly StageChoice[] | null>(null);

  function close(apply: boolean): void {
    if (apply && draft !== null) props.onCommit(draft);
    setDraft(null);
  }

  return (
    <>
      <button
        ref={setTrigger}
        type="button"
        className="pane-editable"
        aria-label={`Stages ${props.fund.name} invests and leads at`}
        onClick={() => setDraft(stageChoicesOf(props.fund))}
      >
        <StageChips stages={props.fund.stages} />
      </button>
      {draft !== null && trigger !== null && (
        <Popover
          anchor={{ kind: 'element', element: trigger }}
          onClose={(reason) => close(reason !== 'escape')}
        >
          <StagePicker
            choices={draft}
            onChange={setDraft}
            onCommit={() => close(true)}
            onRevert={() => close(false)}
          />
        </Popover>
      )}
    </>
  );
}

/** A website reads better as the site it is than as a whole URL. */
export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
